import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Fab,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import { formatDateAndTime } from "../../../../utils/luxon";

interface EventDetailsDialogProps {
  event: any;
  onClose: () => void;
  onReallocate: () => void;
  loading: boolean;
}

export function EventDetailsDialog({ event, onClose, onReallocate, loading }: EventDetailsDialogProps) {
  if (!event) return null;

  const { title, extendedProps } = event;
  const { driverName, jobDetails, timeOffDetails } = extendedProps;
  const date = new Date(event.start).toISOString();

  const isJob = !!jobDetails;
  const isTimeOff = !!timeOffDetails;

  return (
    <Dialog open={!!event} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: isTimeOff ? "error.main" : "primary.main", color: "white", py: 2 }}>
        {isTimeOff ? "Event Information" : "Job Information"}
      </DialogTitle>
      <DialogContent sx={{ px: 4, py: 3, marginTop: 2 }}>
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            {isTimeOff ? "Event Details" : "Job Information"}
          </Typography>
          {!isTimeOff && (
            <Button onClick={onReallocate} variant="contained" color="primary" disabled={loading}>
              {loading && <CircularProgress size={14} style={{ marginRight: "10px" }} color="inherit" />}
              Reallocate
            </Button>
          )}
        </Box>

        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PersonIcon sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Driver
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ ml: 3, color: "text.primary" }}>
            {driverName || "N/A"}
          </Typography>
        </Box>

        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CalendarTodayIcon sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Date
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ ml: 3, color: "text.primary" }}>
            {formatDateAndTime(date)}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {isTimeOff ? (
          <>
            <Typography variant="h6" mb={2} fontWeight="bold">
              Event Details
            </Typography>
            <Typography variant="body1" sx={{ color: "text.primary" }}>
              <strong>Reason:</strong> {timeOffDetails?.notes || "N/A"}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.primary" }}>
              <strong>Start Date:</strong> {formatDateAndTime(timeOffDetails?.startDate)}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.primary" }}>
              <strong>End Date:</strong> {formatDateAndTime(timeOffDetails?.endDate)}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" mb={2} fontWeight="bold">
              Job Details
            </Typography>
            <Typography variant="body1" sx={{ color: "text.primary" }}>
              <strong>Description:</strong> {jobDetails?.description || "N/A"}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 2, justifyContent: "flex-end" }}>
        <Fab
          variant="extended"
          size="medium"
          aria-label="Close2"
          className="blackBackButton"
          color="secondary"
          sx={{ fontWeight: "bold" }}
          onClick={onClose}
        >
          Close
        </Fab>
      </DialogActions>
    </Dialog>
  );
}
